import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import { useUpsellTracking } from 'hooks/useUpsellTracking';
import MainLayout from '../../components/layout/Main';
import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import TestimonialCard from '../../components/molecules/testimonialCard';
import paychexLogo from '../../assets/companies/paychex.svg';
import securlyLogo from '../../assets/companies/securly.svg';
import m13Logo from '../../assets/companies/M13.svg';
import boxLogo from '../../assets/companies/box.svg';
import ringcentralLogo from '../../assets/companies/ringcentral.svg';
import medalliaLogo from '../../assets/companies/medallia.svg';
import boulevardLogo from '../../assets/companies/boulevard.svg';
import nutanixLogo from '../../assets/companies/nutanix.svg';
import enrichedCandidateDataImg from '../../assets/find-candidates/enriched-candidate-data.png';
import verifiedProfessionalProfilesImg from '../../assets/find-candidates/verified-professional-profiles.png';
import automatedOutreachCampaignsImg from '../../assets/find-candidates/automated-outreach-campaigns.png';
import aiPoweredSourcingAgentsImg from '../../assets/find-candidates/ai-powered-sourcing-agents.png';
import travisBarkerAvatar from '../../assets/find-candidates/Travis-Barker.png';
import lindseyRobertsAvatar from '../../assets/find-candidates/Lindsey-Roberts.png';

const DEMO_URL = 'https://getro.com/demo';

const Section = ({ children, bg = 'transparent' }) => (
  <Flex
    sx={{
      width: '100%',
      justifyContent: 'center',
      bg,
      px: '24px',
      py: '64px',
    }}
  >
    <Box sx={{ maxWidth: '900px', width: '100%' }}>{children}</Box>
  </Flex>
);

Section.propTypes = {
  children: PropTypes.node.isRequired,
  bg: PropTypes.string,
};

Section.defaultProps = {
  bg: 'transparent',
};

const CtaButton = ({ children, trackDemoClick }) => (
  <Button
    as="a"
    href={DEMO_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackDemoClick('find_candidates')}
    sx={{
      justifyContent: 'center',
      bg: '#4900FF',
      '&:hover': { bg: '#3D00D9' },
      px: '32px',
    }}
  >
    {children}
  </Button>
);

CtaButton.propTypes = {
  children: PropTypes.node.isRequired,
  trackDemoClick: PropTypes.func.isRequired,
};

const TRUSTED_LOGOS = [
  { name: 'Paychex', src: paychexLogo },
  { name: 'Securly', src: securlyLogo },
  { name: 'M13', src: m13Logo },
  { name: 'Box', src: boxLogo },
  { name: 'RingCentral', src: ringcentralLogo },
  { name: 'Medallia', src: medalliaLogo },
  { name: 'Boulevard', src: boulevardLogo },
  { name: 'Nutanix', src: nutanixLogo },
];

const FEATURES = [
  {
    title: 'Enriched with people, company, and time data',
    description:
      'See how candidates progressed over time, where they delivered impact, and what kind of environment shaped that work.',
    image: enrichedCandidateDataImg,
  },
  {
    title: 'Profiles continuously verified and kept fresh',
    description:
      'Careers change. Teams move. 3D data is verified across sources and refreshed intelligently, so profiles reflect current reality, not outdated snapshots.',
    image: verifiedProfessionalProfilesImg,
  },
  {
    title: 'Automate shortlists and personalized outreach',
    description:
      'Send high-conversion campaigns that write themselves and remove the admin work of follow-ups and tracking.',
    image: automatedOutreachCampaignsImg,
  },
  {
    title: 'Let recruiting run itself',
    description:
      'Work side-by-side with Agents that deliver you hire-ready candidates, while you spend more time on the human side of recruiting.',
    image: aiPoweredSourcingAgentsImg,
  },
];

const TESTIMONIALS = [
  {
    quote:
      'With Findem, we’re not just matching keywords — we’re finding people based on what they’ve truly accomplished and their proven ability. It’s made a massive difference.',
    author: 'Travis Baker',
    role: 'VP of Global Talent Acquisition, Boulevard',
    avatarSrc: travisBarkerAvatar,
  },
  {
    quote:
      'With dynamic talent pools, our engagement will be seamless. We’ll never lose track of great candidates again.',
    author: 'Lindsey Roberts',
    role: 'Recruiting Program Manager, Box',
    avatarSrc: lindseyRobertsAvatar,
  },
];

export const FindCandidates = () => {
  const { trackPaywallShown, trackDemoClick } = useUpsellTracking();

  useEffect(() => {
    trackPaywallShown('find_candidates');
  }, [trackPaywallShown]);

  return (
    <MainLayout>
      <MainLayoutHeader title="Find candidates" />
      <Box sx={{ width: '100%', overflowY: 'auto' }}>
        <Section>
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '24px',
              maxWidth: '720px',
              mx: 'auto',
            }}
          >
            <Text sx={{ fontSize: '36px', fontWeight: 700, color: 'text.dark', lineHeight: 1.2, maxWidth: '720px' }}>
              Fill your open roles with smarter data
            </Text>
            <Text sx={{ fontSize: '18px' }}>
              Find your next top performer with a database of 800m+ profiles, each labelled with 3D data to give you,
              and your built-in AI, the context you need.
            </Text>
            <CtaButton trackDemoClick={trackDemoClick}>Find candidates today</CtaButton>
          </Flex>
        </Section>

        <Section bg="neutral.20">
          <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: '32px' }}>
            <Text
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: 'neutral.400',
                textAlign: 'center',
              }}
            >
              Trusted by top investors and companies
            </Text>
            <Flex
              sx={{
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: ['16px 20px', '32px 40px'],
                width: '100%',
                maxWidth: '640px',
              }}
            >
              {TRUSTED_LOGOS.map(({ name, src }) => (
                <Box key={name} as="img" src={src} alt={name} sx={{ maxWidth: ['28%', '20%'], height: 'auto' }} />
              ))}
            </Flex>
          </Flex>
        </Section>

        {FEATURES.map((feature, index) => (
          <Section key={feature.title}>
            <Flex sx={{ flexDirection: index % 2 === 0 ? 'row' : 'row-reverse', gap: '64px', alignItems: 'center' }}>
              <Flex sx={{ flex: 1, flexDirection: 'column', gap: '16px' }}>
                <Text sx={{ fontSize: '28px', fontWeight: 700, color: 'text.dark', lineHeight: 1.2 }}>
                  {feature.title}
                </Text>
                <Text sx={{ fontSize: '16px', color: 'text.main' }}>{feature.description}</Text>
              </Flex>
              <Box
                as="img"
                src={feature.image}
                alt={feature.title}
                sx={{
                  flex: 1,
                  maxHeight: '280px',
                  borderRadius: '12px',
                  objectFit: 'contain',
                  display: ['none', 'block'],
                }}
              />
            </Flex>
          </Section>
        ))}

        <Section>
          <Flex sx={{ flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '24px' }}>
            <Text sx={{ fontSize: '36px', fontWeight: 700, color: 'text.dark' }}>Find the right talent</Text>
            <Text sx={{ fontSize: '16px', color: 'text.main', maxWidth: '720px' }}>
              Find your next top performer with a database of 800m+ profiles, each labelled with 3D data to give you,
              and your built-in AI, the context you need.
            </Text>
            <CtaButton trackDemoClick={trackDemoClick}>Find candidates today</CtaButton>
          </Flex>
        </Section>

        <Section>
          <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: '40px' }}>
            <Flex sx={{ gap: '32px', flexDirection: ['column', 'row'] }}>
              {TESTIMONIALS.map((testimonial) => (
                <Box key={testimonial.author} sx={{ flex: 1 }}>
                  <TestimonialCard
                    quote={testimonial.quote}
                    author={testimonial.author}
                    role={testimonial.role}
                    avatarSrc={testimonial.avatarSrc}
                  />
                </Box>
              ))}
            </Flex>
          </Flex>
        </Section>
      </Box>
    </MainLayout>
  );
};
